import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isoWeek from 'dayjs/plugin/isoWeek'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import 'dayjs/locale/fr'

dayjs.extend(customParseFormat)
dayjs.extend(isoWeek)
dayjs.extend(weekOfYear)
dayjs.extend(quarterOfYear)
dayjs.locale('fr')

/**
 * The shim emits dayjs objects, which is exactly what antd v5 does today, so
 * consumer code that calls `.format()` / `.toDate()` keeps working unchanged.
 * Incoming values may be dayjs, moment, Date or string.
 */
export const toDayjs = (value, format) => {
  if (value == null || value === '') return null
  if (dayjs.isDayjs(value)) return value
  // moment objects expose toDate(); this covers them without importing moment.
  if (typeof value?.toDate === 'function') return dayjs(value.toDate())
  if (value instanceof Date) return dayjs(value)
  if (typeof value === 'string' && format) {
    const parsed = dayjs(value, format, true)
    if (parsed.isValid()) return parsed
  }
  const fallback = dayjs(value)
  return fallback.isValid() ? fallback : null
}

export const DEFAULT_FORMATS = {
  date: 'DD/MM/YYYY',
  week: 'YYYY-wo',
  month: 'MM/YYYY',
  quarter: 'YYYY-[Q]Q',
  year: 'YYYY',
}

export const resolveFormat = ({ format, picker = 'date', showTime }) => {
  if (typeof format === 'string') return format
  if (Array.isArray(format)) return format[0]
  if (typeof format === 'function') return null
  if (showTime) return `${DEFAULT_FORMATS.date} HH:mm`
  return DEFAULT_FORMATS[picker] ?? DEFAULT_FORMATS.date
}

export const formatValue = (value, { format, picker, showTime }) => {
  if (!value) return ''
  if (typeof format === 'function') return format(value)
  return value.format(resolveFormat({ format, picker, showTime }))
}

/** 6x7 grid starting on Monday, matching antd's fr locale panel. */
export const buildMonthGrid = (viewDate) => {
  const start = viewDate.startOf('month').startOf('isoWeek')
  return Array.from({ length: 42 }, (_, index) => start.add(index, 'day'))
}

export const WEEKDAY_LABELS = ['lu', 'ma', 'me', 'je', 've', 'sa', 'di']

export const MONTH_LABELS = Array.from({ length: 12 }, (_, index) =>
  dayjs().month(index).format('MMM')
)

export const isSameUnit = (a, b, picker = 'date') => {
  if (!a || !b) return false
  const unit = picker === 'date' ? 'day' : picker === 'week' ? 'isoWeek' : picker
  return a.isSame(b, unit)
}

export const isWithin = (day, start, end) =>
  Boolean(start && end && day.isAfter(start.startOf('day')) && day.isBefore(end.endOf('day')))

export { dayjs }
